import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {
  Aangifte,
  BokkzIbDataService,
  IncomeTaxEntity,
  LijfrentePremie,
} from "@snelstart-ventures/fiscaal/domain-logic";
import {debounceTime, distinctUntilChanged, filter, map, Observable, shareReplay, Subscription, take} from "rxjs";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type PremieFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
  polisnummer: FormControl<string | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-lijfrente-premies',
  templateUrl: './lijfrente-premies.component.html',
  styleUrls: ['./lijfrente-premies.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiDetailsModule,
    CommonModule
  ]
})
export class LijfrentePremiesComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public index = 0;
  public isPartner = false;
  public isDefAangifte = {} as boolean;
  public heeftVrijvalFor = {} as boolean;

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public formGroup = new FormGroup({
    betaaldePremieLijfrente: new FormArray<PremieFormGroup>([]),
    lijfrentePremiesFOR: new FormArray<PremieFormGroup>([]),
  });

  public omschrijvingLijfrente: SelectFormControlItem<String>[] = [
    {value: "OMZETTING FOR ZONDER STAKING", label: "Omzetting FOR zonder staking"},
    {value: "OMZETTING FOR BIJ STAKING", label: "Omzetting FOR bij staking"},
    {value: "OMZETTING STAKINGSWINST IN LIJFRENTE", label: "Omzetting stakingswinst in lijfrente"},
  ];

  ngOnInit(): void {
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        this.isDefAangifte = entity.taxType === 'DA';
        if (partner) {
          this.handleSwitch(entity, true);
        } else {
          this.handleSwitch(entity, false);
        }
      });
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
      this.heeftWinst(details)
    }
  }

  private resetAndPatchForms(details: any): void {
    this.formGroup.reset({}, { emitEvent: false }); // Prevent reset from emitting events
    this.updateForm(details.box1?.uitgavenVoorInkomensvoorzieningen?.lijfrentePremiesBetaald || [], this.betaaldePremieLijfrente);
    this.updateForm(details.box1?.uitgavenVoorInkomensvoorzieningen?.lijfrentePremiesFOR || [], this.betaaldePremieLijfrenteFor);
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    this.subscribeToBedragChanges(this.betaaldePremieLijfrente);
    this.subscribeToBedragChanges(this.betaaldePremieLijfrenteFor);
  }

  private subscribeToBedragChanges(formArray: FormArray<PremieFormGroup>): void {
    this.changeSubscriptions.add(
      formArray.valueChanges.pipe(
        debounceTime(500),
        map(() => this.saveForm()),
        map(() => formArray.getRawValue().map(item => item.bedrag)),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.dataService.calculateLatestIncomeTaxReturn().subscribe();
      })
    );
  }

  get betaaldePremieLijfrente(): FormArray<PremieFormGroup> {
    return this.formGroup.controls.betaaldePremieLijfrente;
  }

  get betaaldePremieLijfrenteFor(): FormArray<PremieFormGroup> {
    return this.formGroup.controls.lijfrentePremiesFOR;
  }

  addRijPremieLijfrente(): void {
    this.betaaldePremieLijfrente.push(this.createFormGroup(), {emitEvent: false});
  }

  addRijPremieLijfrenteFor(): void {
    this.betaaldePremieLijfrenteFor.push(this.createFormGroup(), {emitEvent: false})
  }

  verwijderRijPremieLijfrente(index: number): void {
    this.betaaldePremieLijfrente.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false})
    this.formGroup.markAsDirty({emitEvent: false})
    this.saveForm();
  }

  verwijderRijPremieLijfrenteFor(index: number): void {
    this.betaaldePremieLijfrenteFor.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false})
    this.formGroup.markAsDirty({emitEvent: false})
    this.saveForm();
  }

  createFormGroup(item?: LijfrentePremie): PremieFormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null),
      polisnummer: new FormControl<string | null>(item?.polisnummer ?? null),
    });
  }

  private updateForm(data : LijfrentePremie[], array: FormArray<PremieFormGroup>): void {
    const lijst = data || [];
    const formArray = array;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }
    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    const lijfrente: LijfrentePremie[] = this.betaaldePremieLijfrente.getRawValue()
      .filter(item => item.omschrijving || item.bedrag !== null)
      .map(item => ({
        omschrijving: item.omschrijving ?? undefined,
        bedrag: item.bedrag ?? undefined,
        polisnummer: item.polisnummer ?? undefined,
      }));

    const lijfrenteFor: LijfrentePremie[] = this.betaaldePremieLijfrenteFor.getRawValue()
      .filter(item => item.omschrijving || item.bedrag !== null)
      .map(item => ({
        omschrijving: item.omschrijving ?? undefined,
        bedrag: item.bedrag ?? undefined,
        polisnummer: item.polisnummer ?? undefined,
      }));

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const update = (aangifte: Aangifte) => ({
        ...aangifte,
        box1: {
          ...aangifte.box1,
          uitgavenVoorInkomensvoorzieningen: {
            ...aangifte.box1?.uitgavenVoorInkomensvoorzieningen,
            lijfrentePremiesBetaald: lijfrente,
            lijfrentePremiesFOR: lijfrenteFor,
          },
        },
      });

      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
      } : update(currentEntity.aangifte);

      return {
        ...currentEntity,
        aangifte: updatedAangifte,
      } as IncomeTaxEntity;
    });
  }

  private heeftWinst(aangifte : Aangifte) {
    this.heeftVrijvalFor = aangifte.box1?.winstUitOnderneming?.vrijvalFORIvmPremieLijfente !== undefined
  }
}
